import { SheetFileError, dateFormat, dateText, numberText, type SheetRow, type SheetTable } from './file';

/**
 * Reads an old Excel file (.xls: Excel 97 to 2003, BIFF8 in a compound file) in the browser, as file.ts reads an .xlsx: every
 * cell as the text it shows (a time as "14:30", a date as "2026-10-30", a number in full), the first visible sheet that has
 * any rows, hidden rows marked. Nothing new is loaded. Excel 95 and older, and files with a password, are refused, saying so.
 * Tested against files xlwt writes (tool/sheets/make_xls.py) and against xlrd's reading of them (tests/xls.test.ts).
 */

const END = 0xfffffffa; // Sector numbers from here up end a chain (ENDOFCHAIN, FREESECT, …).

/** A compound file's streams, by name, read on demand (its FAT, mini FAT and directory). */
function compound(b: Uint8Array): (name: string) => Uint8Array | null {
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const u16 = (o: number) => dv.getUint16(o, true);
  const u32 = (o: number) => dv.getUint32(o, true);
  if (b.length < 512 || u16(0x1c) !== 0xfffe) throw new SheetFileError('notSpreadsheet');
  const shift = u16(0x1e);
  if (shift !== 9 && shift !== 12) throw new SheetFileError('notSpreadsheet');
  const size = 1 << shift;
  const mini = 1 << u16(0x20);
  const sector = (n: number) => {
    const at = (n + 1) * size;
    if (at + size > b.length) throw new SheetFileError('notSpreadsheet');
    return at;
  };
  // Where the FAT's own sectors are: 109 in the header, the rest in a chain of DIFAT sectors.
  const nFat = u32(0x2c);
  const fatAt: number[] = [];
  for (let i = 0; i < 109 && fatAt.length < nFat; i++) fatAt.push(u32(0x4c + 4 * i));
  for (let d = u32(0x44), hops = 0; fatAt.length < nFat && d < END; hops++) {
    if (hops > u32(0x48)) throw new SheetFileError('notSpreadsheet');
    const at = sector(d);
    for (let i = 0; i < size / 4 - 1 && fatAt.length < nFat; i++) fatAt.push(u32(at + 4 * i));
    d = u32(at + size - 4);
  }
  const fat: number[] = [];
  for (const s of fatAt) {
    const at = sector(s);
    for (let i = 0; i < size / 4; i++) fat.push(u32(at + 4 * i));
  }
  const chain = (table: number[], start: number) => {
    const out: number[] = [];
    for (let s = start; s < END; s = table[s]) {
      if (s >= table.length || out.length > table.length) throw new SheetFileError('notSpreadsheet');
      out.push(s);
    }
    return out;
  };
  const join = (parts: Uint8Array[], length?: number) => {
    const all = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
    let at = 0;
    for (const p of parts) {
      all.set(p, at);
      at += p.length;
    }
    return length == null ? all : all.subarray(0, Math.min(length, all.length));
  };
  const big = (start: number, length?: number) => join(chain(fat, start).map((s) => b.subarray(sector(s), sector(s) + size)), length);
  const dir = big(u32(0x30));
  const ddv = new DataView(dir.buffer, dir.byteOffset, dir.byteLength);
  const entries: { name: string; type: number; start: number; size: number }[] = [];
  for (let at = 0; at + 128 <= dir.length; at += 128) {
    const n = Math.min(64, ddv.getUint16(at + 0x40, true));
    entries.push({
      name: new TextDecoder('utf-16le').decode(dir.subarray(at, at + Math.max(0, n - 2))),
      type: dir[at + 0x42],
      start: ddv.getUint32(at + 0x74, true),
      size: ddv.getUint32(at + 0x78, true),
    });
  }
  const root = entries[0];
  if (!root || root.type !== 5) throw new SheetFileError('notSpreadsheet');
  // Streams under the cutoff (4096 bytes) sit in the mini stream, in 64-byte pieces.
  const cutoff = u32(0x38);
  let miniFat: number[] | null = null;
  let miniStream: Uint8Array | null = null;
  return (name) => {
    const e = entries.find((x) => x.type === 2 && x.name.toLowerCase() === name.toLowerCase());
    if (!e) return null;
    if (e.size >= cutoff) return big(e.start, e.size);
    miniFat ??= (() => {
      const raw = u32(0x3c) < END ? big(u32(0x3c)) : new Uint8Array();
      const v = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
      return Array.from({ length: raw.length / 4 }, (_, i) => v.getUint32(4 * i, true));
    })();
    miniStream ??= big(root.start, root.size);
    return join(chain(miniFat, e.start).map((s) => miniStream!.subarray(s * mini, s * mini + mini)), e.size);
  };
}

/** One BIFF record. */
interface Rec {
  type: number;
  data: Uint8Array;
}

const BOF = 0x0809;
const EOF = 0x000a;
const CONTINUE = 0x003c;

function* recordsOf(w: Uint8Array, from = 0): Generator<Rec> {
  for (let at = from; at + 4 <= w.length; ) {
    const type = w[at] | (w[at + 1] << 8);
    const len = w[at + 2] | (w[at + 3] << 8);
    yield { type, data: w.subarray(at + 4, at + 4 + len) };
    at += 4 + len;
  }
}

const le16 = (d: Uint8Array, at: number) => d[at] | (d[at + 1] << 8);
const le32 = (d: Uint8Array, at: number) => (d[at] | (d[at + 1] << 8) | (d[at + 2] << 16) | (d[at + 3] << 24)) >>> 0;
const f64 = (d: Uint8Array, at: number) => new DataView(d.buffer, d.byteOffset + at, 8).getFloat64(0, true);
/** Characters stored one byte each (Latin-1, the low byte of UTF-16) or two (UTF-16). */
const chars = (d: Uint8Array, at: number, n: number, wide: boolean) =>
  wide ? new TextDecoder('utf-16le').decode(d.subarray(at, at + 2 * n)) : String.fromCharCode(...d.subarray(at, at + n));
/** An XLUnicodeString: a 2-byte length, a flags byte, the characters. */
const longString = (d: Uint8Array, at: number) => (d.length < at + 3 ? '' : chars(d, at + 3, le16(d, at), (d[at + 2] & 1) === 1));

/** An RK number: 30 bits of a double or an integer, maybe divided by 100. */
export function rk(v: number): number {
  let n: number;
  if (v & 2) n = (v | 0) >> 2;
  else {
    const dv = new DataView(new ArrayBuffer(8));
    dv.setUint32(4, (v & 0xfffffffc) >>> 0, true);
    n = dv.getFloat64(0, true);
  }
  return v & 1 ? n / 100 : n;
}

/**
 * The shared strings (SST): spread over the record and any CONTINUE records after it; a string's characters can run on into
 * the next piece, which then starts with its own flags byte (one byte or two a character).
 */
export function sharedStrings(pieces: Uint8Array[]): string[] {
  const out: string[] = [];
  let p = 0;
  let d = pieces[0];
  let at = 8;
  const next = () => {
    d = pieces[++p];
    at = 0;
    if (!d) throw new SheetFileError('notSpreadsheet');
  };
  const skip = (n: number) => {
    let left = n;
    while (left > 0) {
      if (at >= d.length) next();
      const step = Math.min(left, d.length - at);
      at += step;
      left -= step;
    }
  };
  const total = pieces.length ? le32(pieces[0], 4) : 0;
  while (out.length < total && (at < d.length || p < pieces.length - 1)) {
    if (at >= d.length) next();
    const n = le16(d, at);
    let flags = d[at + 2];
    at += 3;
    let runs = 0;
    let ext = 0;
    if (flags & 0x08) {
      runs = le16(d, at);
      at += 2;
    }
    if (flags & 0x04) {
      ext = le32(d, at);
      at += 4;
    }
    let s = '';
    for (let got = 0; got < n; ) {
      const wide = (flags & 1) === 1;
      const room = wide ? (d.length - at) >> 1 : d.length - at;
      if (room <= 0) {
        next();
        flags = d[at++];
        continue;
      }
      const take = Math.min(n - got, room);
      s += chars(d, at, take, wide);
      at += wide ? 2 * take : take;
      got += take;
    }
    skip(4 * runs + ext);
    out.push(s);
  }
  return out;
}

/** Reads an .xls file's first visible sheet that has any rows. */
export function readXls(bytes: Uint8Array): SheetTable {
  let stream: (name: string) => Uint8Array | null;
  try {
    stream = compound(bytes);
  } catch {
    throw new SheetFileError('notSpreadsheet');
  }
  const w = stream('Workbook');
  if (!w) throw new SheetFileError(stream('Book') ? 'oldExcel' : 'notSpreadsheet');
  try {
    return workbook(w);
  } catch (e) {
    if (e instanceof SheetFileError) throw e;
    throw new SheetFileError('notSpreadsheet');
  }
}

function workbook(w: Uint8Array): SheetTable {
  const it = recordsOf(w);
  const first = it.next().value as Rec | undefined;
  if (!first || first.type !== BOF) throw new SheetFileError('notSpreadsheet');
  if (le16(first.data, 0) !== 0x0600) throw new SheetFileError('oldExcel');
  let base1904 = false;
  const formats = new Map<number, string>();
  const xfs: number[] = [];
  const sst: Uint8Array[] = [];
  const sheets: { name: string; at: number }[] = [];
  let inSst = false;
  for (const r of it) {
    if (inSst && r.type === CONTINUE) {
      sst.push(r.data);
      continue;
    }
    inSst = false;
    if (r.type === EOF) break;
    switch (r.type) {
      case 0x002f: // FILEPASS: it has a password.
        throw new SheetFileError('locked');
      case 0x0022: // DATEMODE
        base1904 = le16(r.data, 0) === 1;
        break;
      case 0x041e: // FORMAT
        formats.set(le16(r.data, 0), longString(r.data, 2));
        break;
      case 0x00e0: // XF: each style's number format
        xfs.push(le16(r.data, 2));
        break;
      case 0x00fc: // SST
        sst.push(r.data);
        inSst = true;
        break;
      case 0x0085: // BOUNDSHEET: a worksheet (not a chart or a macro sheet) that isn't hidden
        if (r.data[4] === 0 && r.data[5] === 0) sheets.push({ at: le32(r.data, 0), name: chars(r.data, 8, r.data[6], (r.data[7] & 1) === 1) });
        break;
    }
  }
  const strings = sst.length ? sharedStrings(sst) : [];
  const dates = (xf: number) => {
    const id = xfs[xf] ?? 0;
    return dateFormat(id, formats.get(id));
  };
  const read = sheets.map((s) => ({ name: s.name, rows: sheetRows(w, s.at, strings, dates, base1904) }));
  const i = read.findIndex((s) => s.rows.some((r) => r.cells.some((c) => c.trim())));
  if (i < 0) throw new SheetFileError('empty');
  return { rows: read[i].rows, sheetName: read[i].name, otherSheets: read.slice(i + 1).filter((s) => s.rows.some((r) => r.cells.some((c) => c.trim()))).map((s) => s.name) };
}

function sheetRows(w: Uint8Array, at: number, strings: string[], dates: (xf: number) => boolean, base1904: boolean): SheetRow[] {
  const cells = new Map<number, string[]>();
  const hidden = new Set<number>();
  const put = (r: number, c: number, v: string) => {
    const row = cells.get(r) ?? [];
    while (row.length < c) row.push('');
    row[c] = v;
    cells.set(r, row);
  };
  const number = (n: number, xf: number) => (dates(xf) ? dateText(n, base1904) : numberText(n, String(n)));
  let depth = 0;
  // The cell a formula's text result (the STRING record after it) belongs to.
  let formula: { r: number; c: number } | null = null;
  for (const { type, data: d } of recordsOf(w, at)) {
    if (type === BOF) depth++;
    else if (type === EOF && --depth <= 0) break;
    // A chart inside the sheet has records of its own.
    if (depth !== 1) continue;
    switch (type) {
      case 0x00fd: // LABELSST
        put(le16(d, 0), le16(d, 2), (strings[le32(d, 6)] ?? '').trim());
        break;
      case 0x0204: // LABEL
      case 0x00d6: // RSTRING
        put(le16(d, 0), le16(d, 2), longString(d, 6).trim());
        break;
      case 0x0203: // NUMBER
        put(le16(d, 0), le16(d, 2), number(f64(d, 6), le16(d, 4)));
        break;
      case 0x027e: // RK
        put(le16(d, 0), le16(d, 2), number(rk(le32(d, 6)), le16(d, 4)));
        break;
      case 0x00bd: {
        // MULRK: one row, several columns
        const r = le16(d, 0);
        const c0 = le16(d, 2);
        for (let k = 0; 4 + 6 * k + 6 <= d.length - 2; k++) put(r, c0 + k, number(rk(le32(d, 4 + 6 * k + 2)), le16(d, 4 + 6 * k)));
        break;
      }
      case 0x0205: // BOOLERR: a yes or no, or an error (shown empty, as an .xlsx's are)
        put(le16(d, 0), le16(d, 2), d[7] ? '' : d[6] ? 'Yes' : 'No');
        break;
      case 0x0006: {
        // FORMULA: its last result, a number unless its last two bytes say otherwise
        const r = le16(d, 0);
        const c = le16(d, 2);
        if (le16(d, 12) !== 0xffff) put(r, c, number(f64(d, 6), le16(d, 4)));
        else if (d[6] === 0) formula = { r, c };
        else if (d[6] === 1) put(r, c, d[8] ? 'Yes' : 'No');
        break;
      }
      case 0x0207: // STRING: a formula's text result
        if (formula) put(formula.r, formula.c, longString(d, 0).trim());
        formula = null;
        break;
      case 0x0208: // ROW: hidden or not
        if (le32(d, 12) & 0x20) hidden.add(le16(d, 0));
        break;
    }
  }
  return [...cells.keys()].sort((a, b) => a - b).map((r) => ({ number: r + 1, cells: cells.get(r)!, ...(hidden.has(r) ? { hidden: true } : {}) }));
}
